import * as React from 'react';
import { PointData } from 'data/FlyerData';
import { Resolution } from 'data/Resolution';
import { cloneObject } from 'utils';
const MAX_PROXIMITY_TO_POINT = 20;
const MAX_PROXIMITY_TO_PATH = 50;

const snap = (v: number, vs: number[]): number => {
  let res = vs[0];
  let closestDistance = Math.abs(vs[0] - v);
  for (let i = 1; i < vs.length; i++) {
    const distance = Math.abs(vs[i] - v);
    if (distance < closestDistance) {
      closestDistance = distance;
      res = vs[i];
    }
  }
  if (closestDistance < MAX_PROXIMITY_TO_POINT) {
    return res;
  }
  return v;
};

/* desc Static function. Find point on lines nearest test point
   test point pXy with properties .x and .y
   lines defined by array aXys with nodes having properties .x and .y 
   return is object with .x and .y properties and property i indicating nearest segment in aXys 
   and property fFrom the fractional distance of the returned point from aXy[i-1]
   and property fTo the fractional distance of the returned point from aXy[i]   */

interface PointDataOnShape extends PointData {
  index: number;
}

function getClosestPointOnLines(
  pXy: PointData,
  aXys: PointData[]
): PointDataOnShape {
  var minDist;
  var fTo = 0;
  // var fFrom;
  var x = 0;
  var y = 0;
  var i = 0;
  var dist;

  if (aXys.length > 1) {
    for (var n = 1; n < aXys.length; n++) {
      if (aXys[n].x !== aXys[n - 1].x) {
        var a = (aXys[n].y - aXys[n - 1].y) / (aXys[n].x - aXys[n - 1].x);
        var b = aXys[n].y - a * aXys[n].x;
        dist = Math.abs(a * pXy.x + b - pXy.y) / Math.sqrt(a * a + 1);
      } else {
        dist = Math.abs(pXy.x - aXys[n].x);
      }

      // length^2 of line segment
      var rl2 =
        Math.pow(aXys[n].y - aXys[n - 1].y, 2) +
        Math.pow(aXys[n].x - aXys[n - 1].x, 2);

      // distance^2 of pt to end line segment
      var ln2 = Math.pow(aXys[n].y - pXy.y, 2) + Math.pow(aXys[n].x - pXy.x, 2);

      // distance^2 of pt to begin line segment
      var lnm12 =
        Math.pow(aXys[n - 1].y - pXy.y, 2) + Math.pow(aXys[n - 1].x - pXy.x, 2);

      // minimum distance^2 of pt to infinite line
      var dist2 = Math.pow(dist, 2);

      // calculated length^2 of line segment
      var calcrl2 = ln2 - dist2 + lnm12 - dist2;

      // redefine minimum distance to line segment (not infinite line) if necessary
      if (calcrl2 > rl2) {
        dist = Math.sqrt(Math.min(ln2, lnm12));
      }

      if (minDist == null || minDist > dist) {
        if (calcrl2 > rl2) {
          if (lnm12 < ln2) {
            fTo = 0; // nearer to previous point
            // fFrom = 1;
          } else {
            // fFrom = 0; //nearer to current point
            fTo = 1;
          }
        } else {
          // perpendicular from point intersects line segment
          fTo = Math.sqrt(lnm12 - dist2) / Math.sqrt(rl2);
          // fFrom = Math.sqrt(ln2 - dist2) / Math.sqrt(rl2);
        }
        minDist = dist;
        i = n;
      }
    }

    var dx = aXys[i - 1].x - aXys[i].x;
    var dy = aXys[i - 1].y - aXys[i].y;

    x = aXys[i - 1].x - dx * fTo;
    y = aXys[i - 1].y - dy * fTo;
  }

  return { x, y, index: i };
}

function getClosestPoint(
  p: PointData,
  points: PointData[],
  ignorePoint?: PointData
): { point: PointData; distance: number } {
  let closestPoint = points[0] === ignorePoint ? points[1] : points[0];
  let closestDistance = 10000;
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    if (point === ignorePoint) {
      continue;
    } else {
      const distance = getPointsDistance(p, point);
      if (distance < closestDistance) {
        closestPoint = point;
        closestDistance = distance;
      }
    }
  }
  return {
    point: closestPoint,
    distance: closestDistance
  };
}

function getPointsDistance(p1: PointData, p2: PointData): number {
  const a = p1.x - p2.x;
  const b = p1.y - p2.y;
  return Math.sqrt(a * a + b * b);
}

export const ShapeEditor: React.FC<{
  points: PointData[];
  resolution: Resolution;
}> = ({ points, resolution }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const dotRef = React.useRef<HTMLDivElement>(null);
  const [draggedPoint, setDraggedPoint] = React.useState<PointData | undefined>(
    undefined
  );
  const [shiftPressed, setShiftPressed] = React.useState(false);
  const initialDragPosition = React.useRef<PointData>({ x: 0, y: 0 });

  const updateCanvas = React.useCallback(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, resolution.width, resolution.height);

        // BG
        ctx.fillStyle = '#eb3197';
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        ctx.fill();
        // Border
        ctx.strokeStyle = '#eb3197';
        ctx.lineWidth = 5;
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }
  }, [canvasRef, points]);

  // const debouncedUpdateCanvas = useDebouncedCallback(updateCanvas, 20);

  const onMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const scale = rect.width / resolution.width;
        const mousePosition = {
          x: (e.clientX - rect.left) / scale,
          y: (e.clientY - rect.top) / scale
        };

        // snap to existing point
        const res = getClosestPoint(mousePosition, points);
        if (res.distance < MAX_PROXIMITY_TO_POINT) {
          initialDragPosition.current = cloneObject(res.point);
          setDraggedPoint(res.point);
          return;
        } else {
          // get closest point to lines
          const closestPoint = getClosestPointOnLines(
            mousePosition,
            points.concat([points[0]])
          );
          const dist = getPointsDistance(mousePosition, closestPoint);
          if (dist < MAX_PROXIMITY_TO_PATH) {
            initialDragPosition.current = cloneObject(closestPoint);
            // add new point to shape
            points.splice(closestPoint.index, 0, closestPoint);
            setDraggedPoint(closestPoint);
          }
        }
      }
    },
    [canvasRef, points]
  );

  const onMouseMove = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (canvasRef.current && dotRef.current) {
        const dot = dotRef.current;
        const rect = canvasRef.current.getBoundingClientRect();
        const scale = rect.width / resolution.width;
        const mousePosition = {
          x: (e.clientX - rect.left) / scale,
          y: (e.clientY - rect.top) / scale
        };
        let dotPosition: PointData | undefined;

        if (draggedPoint !== undefined) {
          // drag
          const res = getClosestPoint(mousePosition, points, draggedPoint);
          if (res.distance < MAX_PROXIMITY_TO_POINT && points.length > 2) {
            draggedPoint.x = res.point.x;
            draggedPoint.y = res.point.y;
          } else {
            if (shiftPressed) {
              // snap to prev and next point vertically or horizontally
              const index = points.indexOf(draggedPoint);
              const prevPoint =
                index === 0 ? points[points.length - 1] : points[index - 1];
              const nextPoint = points[(index + 1) % points.length];
              const xPositions = [prevPoint.x, nextPoint.x];
              const yPositions = [prevPoint.y, nextPoint.y];
              mousePosition.x = snap(mousePosition.x, xPositions);
              mousePosition.y = snap(mousePosition.y, yPositions);
            }
            draggedPoint.x = mousePosition.x;
            draggedPoint.y = mousePosition.y;
          }
          dotPosition = draggedPoint;
          updateCanvas();
        } else {
          // snap to existing point
          const res = getClosestPoint(mousePosition, points);
          if (res.distance < MAX_PROXIMITY_TO_POINT) {
            dotPosition = res.point;
          } else {
            // get closest point to lines
            const closestPoint = getClosestPointOnLines(
              mousePosition,
              points.concat([points[0]])
            );
            const dist = getPointsDistance(mousePosition, closestPoint);
            if (dist < MAX_PROXIMITY_TO_PATH) {
              dotPosition = closestPoint;
            }
          }
        }

        if (dotPosition) {
          dot.style.display = 'block';
          dot.style.left = `${dotPosition.x * scale}px`;
          dot.style.top = `${dotPosition.y * scale}px`;
        } else {
          dot.style.display = 'none';
        }
      }
    },
    [canvasRef, dotRef, points, shiftPressed, draggedPoint]
  );

  const onMouseUp = React.useCallback(() => {
    if (draggedPoint !== undefined) {
      // combine if close if points > 2
      if (points.length > 2) {
        const res = getClosestPoint(draggedPoint, points, draggedPoint);
        if (res.distance < MAX_PROXIMITY_TO_POINT) {
          // remove draggedPoint
          const index = points.indexOf(draggedPoint);
          points.splice(index, 1);
        }
      }
      updateCanvas();
    }
    setDraggedPoint(undefined);
  }, [draggedPoint, points]);

  const onKeyDown = React.useRef((e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      setShiftPressed(true);
    }
  });
  const onKeyUp = React.useRef((e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      setShiftPressed(false);
    }
  });

  React.useEffect(() => {
    updateCanvas();
    window.addEventListener('keydown', onKeyDown.current);
    window.addEventListener('keyup', onKeyUp.current);
    return () => {
      window.removeEventListener('keydown', onKeyDown.current);
      window.removeEventListener('keyup', onKeyUp.current);
    };
  }, []);

  return (
    <div
      className="shape-editor"
      style={{
        backgroundColor: `rgba(255,255,255, 0.85)`,
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        zIndex: 10
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      <canvas
        ref={canvasRef}
        width={resolution.width}
        height={resolution.height}
        style={{
          pointerEvents: 'none'
        }}
      />
      <div
        ref={dotRef}
        style={{
          position: 'absolute',
          backgroundColor: `var(--accent-color)`,
          width: 20,
          height: 20,
          display: 'none',
          borderRadius: 10,
          transform: 'translate(-10px, -10px)',
          pointerEvents: 'none'
        }}
      />
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/showcase/ShapeEditor.tsx